import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Academy, Coach, Student } from '@court-sync/shared';
import { LogIn, UserPlus } from 'lucide-react';
import { useAuthStore } from '@/store/auth';
import { privateBookingService, type PrivateBookingConfig } from '@/services/privateBooking';
import { studentService } from '@/services/student';
import { PrivateBookingWizard } from '@/pages/public/privateBooking/PrivateBookingWizard';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui';
import { isStarterRequestBookingAcademy } from '@/utils/publicBrowseMode';

type OrgPrivateBookingEmbedProps = {
  academy: Academy;
  academySlug: string;
  buildOrgPath: (subpath: string) => string;
};

export function OrgPrivateBookingEmbed({
  academy,
  academySlug,
  buildOrgPath,
}: OrgPrivateBookingEmbedProps) {
  const user = useAuthStore((s) => s.user);
  const [config, setConfig] = useState<PrivateBookingConfig | null>(null);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ message: string; bookingId?: string } | null>(null);

  const bookPrivatePath = buildOrgPath('/book-private');
  const loginPath = buildOrgPath(
    `/login?returnUrl=${encodeURIComponent(bookPrivatePath)}`
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        setLoading(true);
        setError(null);
        const cfg = await privateBookingService.getConfig(academy.id);
        if (cancelled) return;
        setConfig(cfg);
        if (!cfg.privateBookingEnabled) {
          setCoaches([]);
          return;
        }
        const list = await privateBookingService.listCoaches(academy.id);
        if (!cancelled) setCoaches(list);
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load private booking');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [academy.id]);

  useEffect(() => {
    if (!academy.id || !user) {
      setStudents([]);
      return undefined;
    }
    if (!user.roles?.includes('guardian') && !user.roles?.includes('student')) {
      setStudents([]);
      return undefined;
    }
    let cancelled = false;
    void (async () => {
      const studsRes = await studentService.getStudents(academy.id);
      if (cancelled) return;
      setStudents(studsRes.success && studsRes.data ? studsRes.data : []);
    })();
    return () => {
      cancelled = true;
    };
  }, [academy.id, user]);

  const refreshStudents = useCallback(async () => {
    if (!academy.id || !user) return;
    const studsRes = await studentService.getStudents(academy.id);
    setStudents(studsRes.success && studsRes.data ? studsRes.data : []);
  }, [academy.id, user]);

  if (isStarterRequestBookingAcademy(academy)) {
    return (
      <p className="text-sm text-muted-foreground">
        Private lesson booking online is not available on this plan. Contact the academy to arrange
        lessons.
      </p>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-10" aria-busy="true">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  if (error) {
    return <p className="text-sm text-destructive">{error}</p>;
  }

  if (!config?.privateBookingEnabled) {
    return (
      <p className="text-sm text-muted-foreground">
        Private lesson booking is not enabled for this academy.
      </p>
    );
  }

  if (done) {
    return (
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="space-y-3 p-5 text-center sm:p-6">
          <div
            className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/15 text-lg text-primary"
            aria-hidden
          >
            ✓
          </div>
          <p className="text-sm font-medium text-foreground">{done.message}</p>
          {done.bookingId ? (
            <Button asChild size="sm" variant="outline">
              <Link to={buildOrgPath('/bookings')}>View bookings</Link>
            </Button>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  if (!user) {
    return (
      <Card className="overflow-hidden">
        <CardHeader className="space-y-1 border-b border-border bg-muted/20 px-4 py-4 sm:px-5">
          <h3 className="text-base font-semibold text-foreground">Book a private lesson</h3>
          <CardDescription className="text-xs sm:text-sm">
            Choose a coach and weekly times — we&apos;ll price it from their hourly rate.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 p-4 sm:p-5">
          <ol className="list-decimal space-y-1.5 pl-4 text-xs text-muted-foreground marker:font-medium marker:text-foreground sm:text-sm">
            <li>Sign in or create an academy account.</li>
            <li>Pick who the lessons are for and select a coach.</li>
            <li>Choose time slots and submit your request.</li>
          </ol>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button asChild className="flex-1" size="sm">
              <Link to={loginPath}>
                <LogIn className="mr-2 h-4 w-4" aria-hidden />
                Sign in
              </Link>
            </Button>
            <Button asChild variant="outline" className="flex-1" size="sm">
              <Link to={bookPrivatePath}>
                <UserPlus className="mr-2 h-4 w-4" aria-hidden />
                Create account
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (coaches.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No coaches are available for private booking right now.
      </p>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card p-3 shadow-sm sm:p-4">
      <PrivateBookingWizard
        embedded
        academy={academy}
        academySlug={academySlug}
        config={config}
        coaches={coaches}
        students={students}
        onStudentsRefresh={refreshStudents}
        onComplete={setDone}
      />
    </div>
  );
}
